/**
 * AI Service for Security Posture Analysis
 * Integrates with OpenAI API to provide intelligent security recommendations
 */

const OPENAI_API_KEY = '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';
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions'

/**
 * Available OpenAI models in order of preference for security assessment
 * GPT-4o is prioritized for its advanced reasoning and security expertise
 */
const AVAILABLE_MODELS = [
  'gpt-4o',           // Most advanced model with superior reasoning
  'gpt-4-turbo',      // High performance alternative
  'gpt-4o-mini',      // Cost-effective option
  'gpt-4',            // Reliable fallback
  'gpt-3.5-turbo'     // Basic fallback
];

/**
 * Validates the OpenAI API key format
 * @param {string} apiKey - The API key to validate
 * @returns {boolean} True if the API key format is valid
 */
const validateApiKey = (apiKey) => {
  return apiKey && typeof apiKey === 'string' && apiKey.startsWith('sk-') && apiKey.length > 20;
};

/**
 * Makes an API request with retry logic and model fallback
 * @param {string} prompt - The prompt to send
 * @param {string} model - The model to use
 * @param {number} retryCount - Current retry attempt
 * @returns {Promise<Object>} API response
 */
const makeOpenAIRequest = async (prompt, model = AVAILABLE_MODELS[0], retryCount = 0) => {
  const maxRetries = 3;
  const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff

  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: model,
        messages: [
          {
            role: 'system',
            content: 'You are a senior cybersecurity consultant and CISSP-certified expert with 15+ years of experience in enterprise security assessments, compliance frameworks (NIST, ISO 27001, SOC 2), and threat modeling. You specialize in translating technical security assessments into actionable business recommendations. Analyze security postures with precision, identify critical vulnerabilities, and provide prioritized remediation strategies that balance security effectiveness with business impact. Your recommendations should be specific, measurable, and aligned with industry best practices.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 3000,
        temperature: 0.3
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || response.statusText;
      
      // Handle specific error codes
      if (response.status === 404) {
        // Model not found - try next available model
        const currentModelIndex = AVAILABLE_MODELS.indexOf(model);
        if (currentModelIndex < AVAILABLE_MODELS.length - 1) {
          console.warn(`Model ${model} not available, trying ${AVAILABLE_MODELS[currentModelIndex + 1]}`);
          return makeOpenAIRequest(prompt, AVAILABLE_MODELS[currentModelIndex + 1], 0);
        }
        throw new Error(`All models unavailable. Last error: ${errorMessage}`);
      }
      
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your OpenAI API key.');
      }
      
      if (response.status === 429) {
        // Rate limit - retry with exponential backoff
        if (retryCount < maxRetries) {
          console.warn(`Rate limited, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return makeOpenAIRequest(prompt, model, retryCount + 1);
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      
      if (response.status >= 500) {
        // Server error - retry
        if (retryCount < maxRetries) {
          console.warn(`Server error, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return makeOpenAIRequest(prompt, model, retryCount + 1);
        }
        throw new Error('OpenAI service temporarily unavailable.');
      }
      
      throw new Error(`OpenAI API error: ${response.status} - ${errorMessage}`);
    }

    const data = await response.json();
    
    if (!data.choices || !data.choices[0] || !data.choices[0].message) {
      throw new Error('Invalid response format from OpenAI API');
    }
    
    return data;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    throw error;
  }
};

/**
 * Analyzes security assessment data and generates AI-powered recommendations
 * @param {Object} assessmentData - The security assessment data
 * @param {number} assessmentData.score - Overall security score (0-100)
 * @param {string} assessmentData.riskLevel - Risk level (low, medium, high)
 * @param {Object} assessmentData.categories - Category scores and details
 * @param {Array} assessmentData.recommendations - Existing recommendations
 * @param {Object} assessmentData.responses - User responses to assessment questions
 * @returns {Promise<Object>} AI analysis and recommendations
 */
export const analyzeSecurityPosture = async (assessmentData) => {
  try {
    // Validate API key
    if (!validateApiKey(OPENAI_API_KEY)) {
      console.error('Invalid OpenAI API key format');
      return generateFallbackAnalysis(assessmentData);
    }

    const prompt = createSecurityAnalysisPrompt(assessmentData);
    const data = await makeOpenAIRequest(prompt);
    const aiAnalysis = data.choices[0].message.content;

    // Parse the AI response into structured data
    return parseAIResponse(aiAnalysis, assessmentData);
  } catch (error) {
    console.error('Error analyzing security posture:', error);
    
    // Provide user-friendly error messages
    const userFriendlyError = getUserFriendlyError(error.message);
    
    // Return fallback analysis with error context
    const fallbackAnalysis = generateFallbackAnalysis(assessmentData);
    fallbackAnalysis.error = userFriendlyError;
    fallbackAnalysis.isAIGenerated = false;
    
    return fallbackAnalysis;
  }
};

/**
 * Converts technical error messages to user-friendly ones
 * @param {string} errorMessage - The technical error message
 * @returns {string} User-friendly error message
 */
const getUserFriendlyError = (errorMessage) => {
  if (errorMessage.includes('Invalid API key')) {
    return 'API key issue detected. Please verify your OpenAI API key configuration.';
  }
  if (errorMessage.includes('Rate limit')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (errorMessage.includes('Network error')) {
    return 'Connection issue. Please check your internet connection and try again.';
  }
  if (errorMessage.includes('All models unavailable')) {
    return 'AI service temporarily unavailable. Using local analysis instead.';
  }
  if (errorMessage.includes('temporarily unavailable')) {
    return 'AI service is experiencing issues. Using local analysis instead.';
  }
  return 'AI analysis temporarily unavailable. Using local analysis instead.';
};

/**
 * Creates a detailed prompt for AI analysis based on assessment data
 * @param {Object} assessmentData - The security assessment data
 * @returns {string} Formatted prompt for AI analysis
 */
const createSecurityAnalysisPrompt = (assessmentData) => {
  const { score, riskLevel, categories, recommendations, responses } = assessmentData
  
  // Calculate category performance
  const categoryAnalysis = Object.entries(categories || {}).map(([category, data]) => {
    // data.score is already a percentage from Dashboard calculation
    const percentage = Math.round(data.score || 0)
    const actualScore = data.total > 0 ? Math.round((percentage / 100) * data.total) : 0
    return `${category}: ${percentage}% (${actualScore}/${data.total})`
  }).join(', ')

  // Count answered questions
  const totalQuestions = Object.keys(responses || {}).length
  const positiveAnswers = Object.values(responses || {}).filter(answer => answer === 'yes').length
  
  return `
CONTEXT: You are conducting a comprehensive cybersecurity risk assessment for an organization. Analyze the provided data using industry-standard frameworks (NIST Cybersecurity Framework, ISO 27001, CIS Controls) and provide expert-level security recommendations.

=== ASSESSMENT DATA ===
**Security Maturity Score:** ${score}% (Risk Classification: ${riskLevel.toUpperCase()})
**Assessment Coverage:** ${totalQuestions} security controls evaluated
**Implementation Rate:** ${positiveAnswers}/${totalQuestions} controls implemented (${Math.round((positiveAnswers/totalQuestions)*100)}%)
**Domain Performance:** ${categoryAnalysis}

**Existing Security Measures:** ${recommendations?.length || 0} current recommendations
${recommendations?.map((rec, index) => `${index + 1}. [${rec.category || 'General'}] ${rec.title}: ${rec.description}`).join('\n') || 'No existing security recommendations documented'}

=== ANALYSIS REQUIREMENTS ===
Provide a detailed cybersecurity assessment following enterprise security standards. Focus on:
1. Critical vulnerability identification and risk quantification
2. Compliance gap analysis (NIST, ISO 27001, SOC 2, GDPR)
3. Threat landscape assessment based on current security posture
4. ROI-focused remediation roadmap with business impact analysis
5. Specific technical controls and implementation guidance

**IMPORTANT ANALYSIS RULES:**
- DO NOT recommend improvements for security categories that show 100% completion
- Focus recommendations ONLY on categories with scores below 100%
- For categories at 100%, acknowledge their strength in the strengths section
- Prioritize recommendations based on actual gaps identified in the assessment data
- Base all recommendations on the specific category performance data provided above

=== REQUIRED OUTPUT FORMAT (JSON) ===
{
  "executiveSummary": "Professional 3-4 sentence summary suitable for C-level executives, including overall risk level and key business impacts",
  "securityPosture": {
    "maturityLevel": "Initial|Developing|Defined|Managed|Optimizing",
    "riskScore": "Quantified risk score (1-10)",
    "complianceReadiness": "Percentage ready for major compliance frameworks"
  },
  "criticalFindings": [
    {
      "severity": "Critical|High|Medium|Low",
      "finding": "Specific security gap or vulnerability",
      "businessImpact": "Potential business consequence",
      "cvssScore": "CVSS score if applicable (0-10)"
    }
  ],
  "strengths": ["Specific implemented security controls and their effectiveness"],
  "priorityRecommendations": [
    {
      "title": "Specific, actionable recommendation title",
      "description": "Detailed implementation guidance with technical specifics",
      "priority": "Critical|High|Medium|Low",
      "category": "Access Control|Network Security|Data Protection|Incident Response|Governance|Compliance",
      "framework": "NIST function/category (e.g., PR.AC-1, DE.CM-7)",
      "businessImpact": "Quantified business benefit and risk reduction",
      "implementationEffort": "Hours/Days/Weeks/Months",
      "estimatedCost": "Budget range ($1K-5K, $5K-25K, $25K+)",
      "timeline": "Immediate (0-30 days)|Short-term (1-3 months)|Long-term (3-12 months)",
      "dependencies": ["Prerequisites or dependencies"],
      "successMetrics": ["Measurable outcomes to track implementation success"]
    }
  ],
  "threatLandscape": {
    "primaryThreats": ["Most likely attack vectors based on current gaps"],
    "vulnerabilityExposure": "Assessment of current attack surface",
    "industryRisks": ["Sector-specific threats and compliance requirements"]
  },
  "complianceAnalysis": {
    "nistAlignment": "Percentage alignment with NIST Cybersecurity Framework",
    "iso27001Gaps": ["Specific ISO 27001 control gaps"],
    "regulatoryRisks": ["Potential regulatory compliance issues"],
    "recommendedCertifications": ["Suggested security certifications to pursue"]
  },
  "implementationRoadmap": {
    "phase1": ["Immediate actions (0-90 days) - Critical security gaps"],
    "phase2": ["Short-term initiatives (3-6 months) - Core security improvements"],
    "phase3": ["Long-term strategy (6-12 months) - Advanced security capabilities"]
  },
  "budgetGuidance": {
    "immediateInvestment": "Estimated budget for critical fixes",
    "annualSecurityBudget": "Recommended annual security spending",
    "roiProjections": "Expected return on security investment"
  }
}

=== ANALYSIS GUIDELINES ===
- Base recommendations on actual assessment data, not generic advice
- Prioritize findings by business risk and implementation feasibility
- Include specific technical controls, not just policy recommendations
- Reference relevant compliance frameworks and industry standards
- Provide quantified risk assessments where possible
- Consider organizational maturity and resource constraints
- Focus on measurable security improvements and business outcomes
  `
}

/**
 * Parses AI response and structures it for the application
 * @param {string} aiResponse - Raw AI response
 * @param {Object} originalData - Original assessment data
 * @returns {Object} Structured AI analysis
 */
const parseAIResponse = (aiResponse, originalData) => {
  try {
    // Try to extract JSON from the response
    const jsonMatch = aiResponse.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      const parsedResponse = JSON.parse(jsonMatch[0])
      return {
        ...parsedResponse,
        timestamp: new Date().toISOString(),
        originalScore: originalData.score,
        originalRiskLevel: originalData.riskLevel,
        confidence: 0.85 // AI confidence score
      }
    }
  } catch (error) {
    console.error('Error parsing AI response:', error)
  }
  
  // Fallback: parse text response manually
  return parseTextResponse(aiResponse, originalData)
}

/**
 * Parses text-based AI response when JSON parsing fails
 * @param {string} textResponse - Text AI response
 * @param {Object} originalData - Original assessment data
 * @returns {Object} Structured analysis
 */
const parseTextResponse = (textResponse, originalData) => {
  return {
    executiveSummary: extractSection(textResponse, 'summary') || 'Comprehensive security assessment completed with actionable recommendations for improving organizational cybersecurity posture.',
    securityPosture: {
      maturityLevel: originalData.score >= 80 ? 'Managed' : originalData.score >= 60 ? 'Defined' : originalData.score >= 40 ? 'Developing' : 'Initial',
      riskScore: Math.ceil((100 - originalData.score) / 10),
      complianceReadiness: `${Math.max(originalData.score - 10, 0)}%`
    },
    criticalFindings: [
      {
        severity: originalData.riskLevel === 'high' ? 'Critical' : originalData.riskLevel === 'medium' ? 'High' : 'Medium',
        finding: 'Security gaps identified through assessment analysis',
        businessImpact: 'Potential exposure to cyber threats and compliance risks',
        cvssScore: originalData.riskLevel === 'high' ? '7.5' : originalData.riskLevel === 'medium' ? '5.0' : '3.0'
      }
    ],
    strengths: extractListItems(textResponse, 'strengths') || ['Security assessment framework implemented', 'Risk evaluation process established'],
    priorityRecommendations: [
      {
        title: 'Implement Priority Security Controls',
        description: 'Deploy critical security controls based on assessment findings to address identified vulnerabilities and strengthen security posture.',
        priority: 'High',
        category: 'Governance',
        framework: 'NIST CSF - Protect (PR)',
        businessImpact: 'Reduced cyber risk exposure and improved compliance readiness',
        implementationEffort: 'Weeks',
        estimatedCost: '$5K-25K',
        timeline: 'Short-term (1-3 months)',
        dependencies: ['Management approval', 'Budget allocation'],
        successMetrics: ['Improved security score', 'Reduced critical findings']
      }
    ],
    threatLandscape: {
      primaryThreats: ['Phishing attacks', 'Ransomware', 'Data breaches', 'Insider threats'],
      vulnerabilityExposure: `Current security score of ${originalData.score}% indicates ${originalData.riskLevel} risk exposure`,
      industryRisks: ['Regulatory compliance requirements', 'Industry-specific threat vectors']
    },
    complianceAnalysis: {
      nistAlignment: `${Math.max(originalData.score - 5, 0)}%`,
      iso27001Gaps: ['Access control policies', 'Incident response procedures', 'Risk management framework'],
      regulatoryRisks: ['Potential compliance violations', 'Data protection requirements'],
      recommendedCertifications: ['ISO 27001', 'SOC 2 Type II', 'NIST Cybersecurity Framework']
    },
    implementationRoadmap: {
      phase1: ['Critical security control implementation', 'Incident response plan development'],
      phase2: ['Security awareness training', 'Access control improvements', 'Monitoring enhancement'],
      phase3: ['Advanced threat detection', 'Compliance certification pursuit', 'Security maturity optimization']
    },
    budgetGuidance: {
      immediateInvestment: '$10K-50K for critical security improvements',
      annualSecurityBudget: `${Math.ceil(originalData.score / 10)}% of IT budget recommended for security`,
      roiProjections: 'Expected 3:1 ROI through risk reduction and compliance benefits'
    },
    // Legacy compatibility fields
    summary: extractSection(textResponse, 'summary') || 'AI analysis completed successfully.',
    strengths: extractListItems(textResponse, 'strengths') || ['Security assessment completed'],
    riskAssessment: {
      currentRisk: `Current risk level: ${originalData.riskLevel}`,
      potentialThreats: ['Security gaps identified in assessment'],
      mitigationSteps: ['Implement recommended security controls']
    },
    timestamp: new Date().toISOString(),
    originalScore: originalData.score,
    originalRiskLevel: originalData.riskLevel,
    confidence: 0.75
  }
}

/**
 * Generates fallback analysis when AI service is unavailable
 * @param {Object} assessmentData - Original assessment data
 * @returns {Object} Fallback analysis
 */
const generateFallbackAnalysis = (assessmentData) => {
  const { score, riskLevel, categories } = assessmentData
  
  // Determine strengths and weaknesses based on score
  const strengths = []
  const weaknesses = []
  const recommendations = []
  
  if (score >= 80) {
    strengths.push('Strong overall security posture')
    strengths.push('Good compliance with security best practices')
  } else if (score >= 60) {
    strengths.push('Moderate security controls in place')
    weaknesses.push('Several areas need improvement')
    recommendations.push({
      title: 'Strengthen Security Controls',
      description: 'Focus on improving security controls in identified weak areas.',
      priority: 'medium',
      category: 'general',
      impact: 'Improved security posture',
      effort: 'Medium'
    })
  } else {
    weaknesses.push('Significant security gaps identified')
    weaknesses.push('High risk of security incidents')
    recommendations.push({
      title: 'Immediate Security Review',
      description: 'Conduct immediate review and implementation of critical security controls.',
      priority: 'high',
      category: 'general',
      impact: 'Critical security improvement',
      effort: 'High'
    })
  }
  
  return {
    summary: `Security assessment completed with ${score}% score and ${riskLevel} risk level. ${score >= 70 ? 'Good foundation with room for improvement.' : 'Significant improvements needed to enhance security posture.'}`,
    strengths,
    weaknesses,
    priorityRecommendations: recommendations,
    riskAssessment: {
      currentRisk: `Current risk level is ${riskLevel} based on assessment results.`,
      potentialThreats: ['Unauthorized access', 'Data breaches', 'System vulnerabilities'],
      mitigationSteps: ['Implement multi-factor authentication', 'Regular security updates', 'Employee training']
    },
    complianceGaps: ['Review industry compliance requirements', 'Update security policies'],
    nextSteps: ['Address high-priority recommendations', 'Schedule regular security assessments', 'Implement continuous monitoring'],
    timestamp: new Date().toISOString(),
    originalScore: score,
    originalRiskLevel: riskLevel,
    confidence: 0.7,
    fallback: true
  }
}

/**
 * Helper function to extract sections from text response
 * @param {string} text - Text to search
 * @param {string} section - Section name to extract
 * @returns {string|null} Extracted section content
 */
const extractSection = (text, section) => {
  const regex = new RegExp(`${section}["']?\s*:?\s*["']?([^\n\r"']+)`, 'i')
  const match = text.match(regex)
  return match ? match[1].trim() : null
}

/**
 * Helper function to extract list items from text response
 * @param {string} text - Text to search
 * @param {string} section - Section name to extract
 * @returns {Array} Extracted list items
 */
const extractListItems = (text, section) => {
  const sectionRegex = new RegExp(`${section}["']?\s*:?\s*\[([^\]]+)\]`, 'i')
  const match = text.match(sectionRegex)
  if (match) {
    return match[1].split(',').map(item => item.trim().replace(/["']/g, ''))
  }
  return []
}

/**
 * Generates quick security insights for dashboard display
 * @param {Object} assessmentData - Assessment data
 * @returns {Promise<Object>} Quick insights
 */
export const generateQuickInsights = async (assessmentData) => {
  const { score, riskLevel, categories } = assessmentData
  
  // Find the weakest category
  let weakestCategory = null
  let lowestScore = 100
  
  Object.entries(categories || {}).forEach(([category, data]) => {
    const percentage = data.total > 0 ? (data.score / data.total) * 100 : 0
    if (percentage < lowestScore) {
      lowestScore = percentage
      weakestCategory = category
    }
  })
  
  return {
    overallHealth: score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Poor',
    riskTrend: riskLevel === 'low' ? 'Improving' : riskLevel === 'high' ? 'Concerning' : 'Stable',
    focusArea: weakestCategory || 'General Security',
    quickWin: score < 70 ? 'Enable multi-factor authentication' : 'Regular security training',
    nextAssessment: 'Recommended in 3 months',
    confidence: 0.8
  }
}

export default {
  analyzeSecurityPosture,
  generateQuickInsights
}